import {
  Box,
  Button,
  Center,
  Container,
  Flex,
  Grid,
  GridItem,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Spinner,
  Text,
  VStack,
  useDisclosure,
} from '@chakra-ui/react'
import type { NavigationByIdentifierQuery } from '@stocker/codegen/pimcore'
import { useClassificationSortingQuery } from '@stocker/codegen/pimcore'
import type { AllCollectionsQuery, CollectionQuery } from '@stocker/codegen/vendure'
import {
  useActiveChannelQuery,
  useActiveCustomerQuery,
  useAllCollectionsQuery,
  useCollectionQuery,
  useCustomerSpecificPriceQuery,
} from '@stocker/codegen/vendure'
import { A11CommerceConfig } from '@stocker/commerce-config'
import { VariantConfigurationProvider } from '@stocker/ui-components/custom'
import type { ICurrentRefinemt, IVariant } from '@stocker/ui-components/design-system'
import {
  FilterSlider,
  HeroProductListing,
  HierarchicalMenu,
  InstantSearchPagination,
  Panel,
  ProductCard,
  ProductListingOptionsBar,
  RefinementList,
  Seo,
  ViewStyle,
} from '@stocker/ui-components/design-system'
import {
  A11TypesenseInstantSearchAdapter,
  A11TypesenseOptions,
  PriceType,
  asComponent,
  buildUrl,
  formatHeadlineColor,
  getServerData,
  usePriceType,
} from '@stocker/ui-components/helpers'
import uniqBy from 'lodash/uniqBy'
import type { GetStaticProps } from 'next'
import { useRouter } from 'next/router'
import qs from 'qs'
import type { ParsedUrlQuery } from 'querystring'
import React, { useEffect, useMemo, useState } from 'react'
import type { InstantSearchProps } from 'react-instantsearch-dom'
import {
  InstantSearch,
  connectHierarchicalMenu,
  connectHits,
  connectPagination,
  connectRange,
  connectRefinementList,
} from 'react-instantsearch-dom'
import { useIntl } from 'react-intl'

interface IPageProps extends InstantSearchProps {
  collection: NonNullable<CollectionQuery['collection']>
  allCollections: AllCollectionsQuery
  initialData: CollectionQuery
  searchString: string
  navItems: NavigationByIdentifierQuery
  refinementSortOrder: string[]
}

interface IStaticParams extends ParsedUrlQuery {
  slug: string
}

interface ITypesenseVariant {
  productId: string | number
  collectionSlugs: string[]
  channelId: string
  languageCode?: string
  productVariantId?: number
  id: string | number
  productName: string
  productVariantPreview: string
  productPreview: string
  slug: string
  description: string
  currencyCode: string
  name: string
  sku: string
  price: number
  inStock?: boolean
  customMapping_allVariantOptions?: string
  customMapping_facets: string[]
  customMapping_stockStatus: string
  customMapping_systemName: string
}

interface ITypesenseVariantOption {
  id: string | number
  sku: string
  stockStatus: string
  options: Array<{
    code: string
    group: {
      code: string
    }
  }>
}

export const getStaticPaths = async () => {
  const allCollections = await useAllCollectionsQuery.fetcher()()

  return {
    paths: allCollections.collections.items.map((collection) => ({
      params: { slug: collection.slug || '404' },
    })),
    fallback: 'blocking',
  }
}

const pathToSearchState = (path = '') =>
  path.includes('?') ? qs.parse(path.substring(path.indexOf('?') + 1)) : {}
const createURL = (state: string) => `?${qs.stringify(state)}`

export const getStaticProps: GetStaticProps = async (context) => {
  const { slug } = context.params as IStaticParams

  console.log(`[Category] getStaticProps called for slug: "${slug}"`)

  let serverData
  let data
  let allCollections

  try {
    serverData = await getServerData()
  } catch (error) {
    console.error(`[Category] Error fetching serverData for slug "${slug}":`, error)
    throw error
  }

  try {
    data = await useCollectionQuery.fetcher({ slug })()
    console.log(`[Category] Collection query result for slug "${slug}":`, {
      found: !!data.collection,
      collectionId: data.collection?.id,
      collectionName: data.collection?.name,
    })
  } catch (error) {
    console.error(`[Category] Error fetching collection for slug "${slug}":`, error)
    throw error
  }

  try {
    allCollections = await useAllCollectionsQuery.fetcher()()
    console.log(`[Category] All collections count: ${allCollections.collections.items.length}`)
  } catch (error) {
    console.error(`[Category] Error fetching allCollections for slug "${slug}":`, error)
    throw error
  }

  const searchActiveCategory: string[] = []

  data.collection?.breadcrumbs
    .filter((x) => x.slug !== '__root_collection__')
    .forEach((element) => {
      searchActiveCategory.push(element.name)
    })

  const searchString = searchActiveCategory.join(' > ')

  let refinementSortOrder: string[] = []
  try {
    refinementSortOrder =
      (
        await useClassificationSortingQuery.fetcher({ defaultLanguage: 'de' })()
      ).getWebsiteConfigurationListing?.edges?.[0]?.node?.sortingClassification
        ?.map((classification) => classification?.name ?? '')
        .filter((e) => e) ?? []
  } catch (error) {
    console.error(`[Category] Error fetching classification sorting for slug "${slug}":`, error)
    // Continue without sorting, non-critical
  }

  // TODO: [AC-221] Implement SSR for InstantSearch
  // const resultsState = await findResultsState(CategoryPage, {
  //   searchClient: typesenseInstantsearchAdapter.searchClient,
  //   indexName: process.env.NEXT_PUBLIC_VENDURE_PRODUCT_INDEX,
  //   searchState,
  //   collection: data.collection,
  //   allCollections: allCollections,
  //   searchString: searchString,
  //   initialData: data,
  // })

  // handle 404
  if (!data.collection) {
    console.warn(`[Category] 404 - Collection not found for slug: "${slug}"`, {
      availableSlugs: allCollections.collections.items.slice(0, 10).map(c => c.slug),
      totalCollections: allCollections.collections.items.length,
    })
    return {
      notFound: true,
      revalidate: 600,
    }
  }

  return {
    props: {
      // resultsState: JSON.parse(JSON.stringify(resultsState)),
      refinementSortOrder,
      revalidateTime: Date.now(),
      collection: data.collection,
      allCollections,
      initialData: data,
      searchString,
      ...serverData, // Data needed for _app.tsx Header, Footer etc
    },
    revalidate: 600,
  }
}

// Function as component - this is need to group variants by productId, this prevents that all variants are displayed
const LocalHits = ({
  hits,
  viewStyle,
  currentRefinement,
  searchString,
  isLoading,
}: {
  hits?: ITypesenseVariant[]
  viewStyle?: ViewStyle
  searchString?: string
  currentRefinement?: ICurrentRefinemt
  isLoading?: boolean
}) => {
  const groupedHits = uniqBy(hits, 'productId')

  if (isLoading) {
    return (
      <Center padding={7}>
        <Spinner />
      </Center>
    )
  }

  return (
    <Grid
      templateColumns={{ base: 'repeat(1, 1fr)', lg: 'repeat(2, 1fr)', xl: 'repeat(3, 1fr)' }}
      gap={5}
    >
      {groupedHits.map((product) => {
        const variants = hits
          ?.filter(
            (hit) =>
              hit.productId === product.productId &&
              hit.channelId === product.channelId &&
              hit.languageCode === product.languageCode,
          )
          .sort((a, b) => a.price - b.price)
        return (
          <GridItem
            key={`product-${String(product.productId)}`}
            colSpan={{ base: 3, lg: viewStyle === ViewStyle.Grid ? 1 : 3 }}
            w="100%"
          >
            <LocalProductCard
              viewStyle={viewStyle}
              hit={product}
              variants={variants ?? []}
              currentRefinement={currentRefinement}
              searchString={searchString}
            />
          </GridItem>
        )
      })}
    </Grid>
  )
}

const LocalProductCard = ({
  hit,
  variants,
  viewStyle,
  currentRefinement,
  searchString,
}: {
  hit: ITypesenseVariant
  variants: ITypesenseVariant[]
  viewStyle?: ViewStyle
  searchString?: string
  currentRefinement?: ICurrentRefinemt
}) => {
  const [variant, setVariant] = useState(variants[0] as unknown as IVariant)
  const { priceType } = usePriceType()
  const { data: activeChannel } = useActiveChannelQuery()
  const { data: user } = useActiveCustomerQuery()

  const allVariantOptions = JSON.parse(hit.customMapping_allVariantOptions ?? '[]')
  const variantsMapped = allVariantOptions.map((variant: ITypesenseVariantOption) => {
    return {
      productVariantId: variant.id,
      name: hit.productName,
      sku: variant.sku,
      stockLevel: variant.stockStatus
        ? variant.stockStatus
        : hit.inStock
        ? ('IN_STOCK' as const)
        : ('OUT_OF_STOCK' as const),
      currencyCode: hit.currencyCode,
      category: hit.collectionSlugs,
      options: variant.options.map((option) => {
        const optionCode = Number(option.code)
        if (option.group.code === 'dn' && !isNaN(optionCode)) {
          // currentRefinement is filter slider values
          const minDn = currentRefinement?.min
          const maxDn = currentRefinement?.max
          if (minDn && maxDn) {
            if (!(optionCode >= minDn && optionCode <= maxDn)) {
              return {
                code: '',
                group: {
                  name: '',
                },
              }
            }
          }
        }

        return {
          code: option.code,
          group: {
            name: option.group.code,
          },
        }
      }),
    }
  })

  const { data: customerSpecificPrice } = useCustomerSpecificPriceQuery({
    channelCode: activeChannel?.activeChannel.code ?? '__default_channel__',
    variantId: String(variant.productVariantId),
  })

  const basePrice = customerSpecificPrice?.customerSpecificPrice?.basePrice
  const discountPrice = customerSpecificPrice?.customerSpecificPrice?.price

  return (
    <ProductCard
      heading={{
        text: (
          <Text>
            {hit.customMapping_systemName && (
              <Text display="inline">{formatHeadlineColor(hit.customMapping_systemName)} </Text>
            )}
            {formatHeadlineColor(hit.productName)}
          </Text>
        ),
        size: 'lg',
        type: 'h3',
      }}
      userLoggedIn={!!user?.activeCustomer}
      variants={variantsMapped}
      layout={viewStyle === ViewStyle.Grid ? 'grid' : 'list'}
      sku={variant.sku}
      image={{
        fullpath: `${process.env.NEXT_PUBLIC_VENDURE_ASSET_URL}/${
          !hit.productPreview ? hit.productVariantPreview : hit.productPreview
        }?width=500`,
      }}
      description={hit.description}
      detailLink={buildUrl('product', `${hit.slug}?sku=${variant.sku}`)}
      hideResetButton
      basePrice={basePrice}
      discountPrice={
        priceType === PriceType.Base ? undefined : basePrice === discountPrice ? 0 : discountPrice
      }
      // here we check the a11 config if we should only show the stock status when the user is logged in
      hideStock={
        A11CommerceConfig.product.onlyShowStockStatusWhenLoggedIn ? !user?.activeCustomer : false
      }
      // hidePrice={user?.activeCustomer === null} TODO move this to A11 Config
      onVariantChange={(variant) => {
        if (variant) {
          setVariant(variant)
        }
      }}
      searchString={searchString}
      currentRefinemt={currentRefinement}
    />
  )
}

// const CustomClearRefinements = connectCurrentRefinements(ClearRefinementsButton as any)
const CustomRangeSlider = connectRange(FilterSlider)
const CustomHierarchicalMenu = connectHierarchicalMenu(HierarchicalMenu)
const CustomRefinementList = connectRefinementList(RefinementList)
const CustomHits = connectHits(LocalHits) as unknown as typeof LocalHits
const CustomPagination = connectPagination(InstantSearchPagination)

const CategoryPage: React.FC<IPageProps> = ({
  searchClient,
  resultsState,
  widgetsCollector,
  collection,
  allCollections,
  searchString,
  navItems,
  refinementSortOrder,
}) => {
  const intl = useIntl()
  const router = useRouter()
  const [isLoading, setIsLoading] = useState(false)
  const [viewStyle, setViewStyle] = useState<ViewStyle>(ViewStyle.Grid)
  const [defaultHierarchicalMenuValue, setDefaultHierarchicalMenuValue] = useState<string | null>(
    null,
  )
  const searchState = pathToSearchState(router.asPath)
  const [localSearchState, setLocalSearchState] = useState<any | null>()

  const typesenseInstantsearchAdapter = useMemo(() => {
    return new A11TypesenseInstantSearchAdapter(router, A11TypesenseOptions)
  }, [router])

  // Create breadcrumb for typesense
  const collectionsBreadcrumb = useMemo(() => {
    return allCollections.collections.items.map((collection) => {
      const breadcrumb = collection.breadcrumbs
        .filter((x) => x.slug !== '__root_collection__')
        .map((element) => element.name)

      return {
        collection,
        breadcrumb: breadcrumb.join(' > '),
      }
    })
  }, [allCollections.collections.items])

  if (!localSearchState) {
    setLocalSearchState({
      page: searchState.page ?? 1,
      sortby: searchState.sortby ?? 'vendure_products/sort/customMapping_sortIndex:asc',
      hitsPerPage: searchState.hitsPerPage ?? 12,
    })
  }

  useEffect(() => {
    setDefaultHierarchicalMenuValue(searchString)
  }, [searchString])

  const { isOpen, onOpen, onClose } = useDisclosure()

  const [currentRefinement, setCurrentRefinement] = useState<ICurrentRefinemt>()

  const filterStack = (condition: boolean) => {
    return (
      <VStack spacing="15px" minW={{ base: '100px', lg: '350px' }} pb={6}>
        {/* TODO: [AC-236] refactor sidebar to A11 Commerce config */}
        {/* Config for filters */}
        {condition && (
          <Panel
            heading={{
              text: intl.formatMessage({ id: 'category-page--filter-category' }),
              size: 'md',
            }}
          >
            <CustomHierarchicalMenu
              searchClient={searchClient}
              navItems={navItems}
              rootPath="Produkte"
              className="mt-3"
              defaultRefinement={defaultHierarchicalMenuValue}
              path
              attributes={[
                'customMapping_collectionBreadcrumbslvl0',
                'customMapping_collectionBreadcrumbslvl1',
                'customMapping_collectionBreadcrumbslvl2',
                'customMapping_collectionBreadcrumbslvl3',
              ]}
              showParentLevel={true}
              limit={50}
            />
          </Panel>
        )}
        <Panel
          heading={{
            text: intl.formatMessage({ id: 'category-page--filter-classification' }),
            size: 'md',
          }}
        >
          <CustomRefinementList
            attribute="customMapping_classification"
            sortOrder={refinementSortOrder}
          />
        </Panel>
        <CustomRangeSlider
          heading={{
            text: intl.formatMessage({ id: 'category-page--filter-diameter' }),
            size: 'md',
          }}
          attribute="customMapping_diameterMax"
          setCurrentRefinement={setCurrentRefinement}
        />
        {/* <CustomClearRefinements/> */}
      </VStack>
    )
  }

  return (
    <VariantConfigurationProvider
      value={{
        diameter: collection.customFields?.preselectedDiameter,
      }}
    >
      <Seo title={collection.name} description={collection.description} />
      <Container maxW="container.page" py={{ base: '20px', lg: '35px' }}>
        <InstantSearch
          indexName={process.env.NEXT_PUBLIC_VENDURE_PRODUCT_INDEX!}
          searchClient={typesenseInstantsearchAdapter.searchClient}
          searchState={localSearchState}
          resultsState={resultsState}
          onSearchStateChange={(nextSearchState) => {
            // resolve selected collection and redirect
            if (nextSearchState?.hierarchicalMenu?.customMapping_collectionBreadcrumbslvl0) {
              const breadcrumb =
                nextSearchState.hierarchicalMenu.customMapping_collectionBreadcrumbslvl0
              const selectedCategory = collectionsBreadcrumb.find((element) => {
                return element.breadcrumb.trim() === breadcrumb.trim()
              })

              if (selectedCategory?.collection) {
                setIsLoading(true)
                window.location.href = buildUrl('category', selectedCategory.collection.slug)

                if (localSearchState) {
                  localSearchState.page = 1
                }

                setLocalSearchState(localSearchState)
              }
              return
            }

            router.push({
              pathname: window.location.pathname,
              query: {
                page: nextSearchState.page ?? 1,
                sortBy:
                  nextSearchState.sortBy ?? 'vendure_products/sort/customMapping_sortIndex:asc',
                hitsPerPage: nextSearchState.hitsPerPage ?? 12,
              },
            })

            setLocalSearchState(nextSearchState)
          }}
          createURL={createURL}
          widgetsCollector={widgetsCollector}
        >
          {/* Hero - Start */}
          <Flex gap={{ base: 0, lg: '15px' }} mb="30px">
            <Box minW={{ base: '0', lg: '350px' }} />
            <Box width="100%">
              <HeroProductListing
                heading={{ text: collection.name }}
                text={collection.description}
                backgroundImage={
                  collection.assets.length > 0
                    ? { fullpath: collection.assets[0].source }
                    : { fullpath: '' }
                }
                link={
                  collection.customFields?.hasDetailPage
                    ? {
                        absolutePath: `/category/${collection.slug}/information`,
                        text: intl.formatMessage({ id: 'category-page--more-information' }),
                      }
                    : undefined
                }
              />
            </Box>
          </Flex>
          {/* Hero - End */}
          {/* Productlisting - Start */}
          <Flex gap="15px" flexDirection={{ base: 'column', md: 'row' }}>
            <Box display={{ base: 'none', md: 'initial' }}>{filterStack(!isOpen)}</Box>
            <Box display={{ base: 'initial', md: 'none' }}>
              <Button colorScheme="accent" w="100%" onClick={onOpen}>
                {intl.formatMessage({ id: '--filter' })}
              </Button>
              <Modal size="full" isOpen={true} onClose={onClose} blockScrollOnMount={false}>
                <Box display={isOpen ? 'initial' : 'none'}>
                  <ModalOverlay />
                  <ModalContent>
                    <ModalHeader px="15px">{intl.formatMessage({ id: '--filter' })}</ModalHeader>
                    <ModalCloseButton mt="5px" />
                    <ModalBody px="15px">{filterStack(isOpen)}</ModalBody>
                    <ModalFooter>
                      <Button
                        position="fixed"
                        bottom="0"
                        left="50%"
                        transform="translate(-50%)"
                        alignSelf="center"
                        w="100%"
                        colorScheme="accent"
                        mr={3}
                        onClick={onClose}
                      >
                        {intl.formatMessage({ id: '--close' })}
                      </Button>
                    </ModalFooter>
                  </ModalContent>
                </Box>
              </Modal>
            </Box>
            <Box width="100%">
              <ProductListingOptionsBar
                pagination={{ currentPage: 1, maxPages: 12 }}
                sortTypes={[
                  {
                    label: intl.formatMessage({ id: 'category-page--sortby-relevance' }),
                    value: 'vendure_products/sort/customMapping_sortIndex:asc',
                  },
                  {
                    label: intl.formatMessage({ id: 'category-page--sortby-price-asc' }),
                    value: 'vendure_products/sort/price:asc',
                  },
                  {
                    label: intl.formatMessage({ id: 'category-page--sortby-price-desc' }),
                    value: 'vendure_products/sort/price:desc',
                  },
                  {
                    label: intl.formatMessage({ id: 'category-page--sortby-sku-asc' }),
                    value: 'vendure_products/sort/sku:asc',
                  },
                  {
                    label: intl.formatMessage({ id: 'category-page--sortby-sku-desc' }),
                    value: 'vendure_products/sort/sku:desc',
                  },
                  {
                    label: intl.formatMessage({ id: 'category-page--sortby-name-asc' }),
                    value: 'vendure_products/sort/customMapping_sortName:asc',
                  },
                  {
                    label: intl.formatMessage({ id: 'category-page--sortby-name-desc' }),
                    value: 'vendure_products/sort/customMapping_sortName:desc',
                  },
                ]}
                onViewStyleChange={(payload) => {
                  setViewStyle(payload)
                }}
                viewStyle={viewStyle}
              />
              <CustomHits
                isLoading={isLoading}
                viewStyle={viewStyle}
                currentRefinement={currentRefinement}
                searchString={searchString}
              />
              <Box display="flex" justifyContent="center" mt="25px">
                <CustomPagination />
              </Box>
            </Box>
          </Flex>
          {/* Productlisting - End */}
          {/* SEO Text - Start */}
          {collection.customFields?.seoDescription && (
            <Flex
              my={{ base: '15', md: '30px' }}
              ml={{ base: 0, md: '365px' }}
              maxW="container.content"
            >
              <Box>{asComponent(collection.customFields.seoDescription)}</Box>
            </Flex>
          )}
          {/* SEO Text - End */}
        </InstantSearch>
      </Container>
    </VariantConfigurationProvider>
  )
}

export default CategoryPage
